import { useEffect, useState } from "react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { toast } from "react-toastify";
import { actualizarChecklistTramite } from "../../services/tramiteService";

// Ítems del checklist en el orden en que se muestran. `campo` es el nombre
// del flag tanto en el trámite (tramite.dniOk, etc.) como en el payload que
// espera el endpoint PATCH /api/tramites/{id}/checklist.
const ITEMS = [
    { campo: "dniOk", label: "Fotocopia DNI" },
    { campo: "pactoOk", label: "Pacto OK" },
    { campo: "caratulaOk", label: "Carátula" },
];

function ItemChecklist({ campo, label, activo, disabled, onToggle }) {
    return (
        <div
            className="d-inline-flex align-items-center gap-2 rounded-pill px-3 py-2"
            role="button"
            aria-disabled={disabled}
            onClick={() => {
                if (!disabled) onToggle(campo);
            }}
            style={{
                cursor: disabled ? "wait" : "pointer",
                border: `2px solid ${activo ? "#198754" : "#dee2e6"}`,
                background: activo ? "#f0fdf4" : "#f9fafb",
                opacity: disabled ? 0.7 : 1,
                transition: "all 0.2s ease",
                userSelect: "none",
            }}
        >
            {/* Toggle visual */}
            <div style={{
                width: 36, height: 20, borderRadius: 10,
                background: activo ? "#198754" : "#d1d5db",
                position: "relative", transition: "background 0.25s", flexShrink: 0,
            }}>
                <div style={{
                    width: 14, height: 14, borderRadius: "50%", background: "#fff",
                    position: "absolute", top: 3,
                    left: activo ? 18 : 3,
                    transition: "left 0.25s",
                    boxShadow: "var(--shadow-sm)",
                }} />
            </div>
            <span style={{
                fontWeight: 600, fontSize: "0.88rem",
                color: activo ? "#166534" : "#6b7280",
                transition: "color 0.2s",
            }}>
                {label}
            </span>
            {activo && (
                <CheckCircleIcon fontSize="small" style={{ color: "#198754" }} />
            )}
        </div>
    );
}

/**
 * Checklist de documentación del trámite: Fotocopia DNI, Pacto OK y Carátula.
 * Reemplaza al toggle de "Pacto OK" que antes viajaba en el PUT completo del
 * trámite (con control de concurrencia por versión) y por eso terminaba en
 * 409 con cualquier desincronización de `tramite.version`. Este componente
 * usa el endpoint PATCH dedicado (actualizarChecklistTramite), que solo toca
 * estos tres booleanos y no valida versión.
 *
 * UI optimista: el toggle cambia al instante y se revierte si el PATCH falla.
 */
export default function ChecklistTramite({ tramite, onActualizado }) {
    const [valores, setValores] = useState({
        dniOk: !!tramite?.dniOk,
        pactoOk: !!tramite?.pactoOk,
        caratulaOk: !!tramite?.caratulaOk,
    });
    const [guardando, setGuardando] = useState(false);

    useEffect(() => {
        setValores({
            dniOk: !!tramite?.dniOk,
            pactoOk: !!tramite?.pactoOk,
            caratulaOk: !!tramite?.caratulaOk,
        });
    }, [tramite]);

    const handleToggle = async (campo) => {
        if (!tramite?.idTramite || guardando) return;

        const nuevoValor = !valores[campo];
        const valoresPrevios = valores;

        setGuardando(true);
        setValores((prev) => ({ ...prev, [campo]: nuevoValor }));

        try {
            await actualizarChecklistTramite(tramite.idTramite, { [campo]: nuevoValor });
            await onActualizado?.();
        } catch (error) {
            setValores(valoresPrevios);
            toast.error(`Error al actualizar el checklist: ${error.response?.data?.message || error.message}`);
        } finally {
            setGuardando(false);
        }
    };

    if (!tramite) return null;

    return (
        <div className="mt-3 d-flex flex-wrap gap-2">
            {ITEMS.map(({ campo, label }) => (
                <ItemChecklist
                    key={campo}
                    campo={campo}
                    label={label}
                    activo={!!valores[campo]}
                    disabled={guardando}
                    onToggle={handleToggle}
                />
            ))}
        </div>
    );
}
